import React, { useEffect, useRef } from "react";

// Canvas visualization: trade count (bars) + win rate (line) + avg R (bars, signed) per confluence band.
export default function BandChart({ bands }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth, H = canvas.clientHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const mono = "'JetBrains Mono', monospace";
    const padL = 36, padR = 36, padT = 18, padB = 26;
    const n = bands.length, gw = (W - padL - padR) / n;
    const maxN = Math.max(1, ...bands.map((b) => b.trades));
    const half = (H - padT - padB) / 2;
    const midY = padT + half; // zero line for avg R
    const maxR = Math.max(0.5, ...bands.map((b) => Math.abs(b.avg_r ?? 0)));

    ctx.strokeStyle = "rgba(255,255,255,0.08)"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(padL, midY); ctx.lineTo(W - padR, midY); ctx.stroke();
    ctx.font = `9px ${mono}`; ctx.fillStyle = "#7C879E"; ctx.textAlign = "right"; ctx.textBaseline = "middle";
    ctx.fillText(`+${maxR.toFixed(1)}R`, padL - 4, padT + 4);
    ctx.fillText("0R", padL - 4, midY);
    ctx.fillText(`-${maxR.toFixed(1)}R`, padL - 4, H - padB - 4);
    ctx.textAlign = "left";
    ctx.fillText("n", W - padR + 4, padT + 4);

    bands.forEach((b, i) => {
      const x0 = padL + i * gw;
      const cx = x0 + gw / 2;
      // trade count: faint bar from the bottom
      const hN = (b.trades / maxN) * (H - padT - padB) * 0.9;
      ctx.fillStyle = "rgba(245,166,35,0.10)";
      ctx.fillRect(x0 + 4, H - padB - hN, gw - 8, hN);
      // avg R: signed bar around the zero line
      if (b.trades > 0 && b.avg_r != null) {
        const hR = (Math.abs(b.avg_r) / maxR) * half * 0.9;
        ctx.fillStyle = b.avg_r >= 0 ? "rgba(0,192,118,0.85)" : "rgba(255,59,48,0.85)";
        ctx.fillRect(cx - gw * 0.22, b.avg_r >= 0 ? midY - hR : midY, gw * 0.44, hR);
        ctx.fillStyle = "#E5E7EB"; ctx.textAlign = "center"; ctx.textBaseline = "bottom"; ctx.font = `10px ${mono}`;
        ctx.fillText(`${b.avg_r >= 0 ? "+" : ""}${b.avg_r.toFixed(2)}R`, cx, (b.avg_r >= 0 ? midY - hR : midY) - 3);
      }
      ctx.fillStyle = "#7C879E"; ctx.textAlign = "center"; ctx.textBaseline = "top"; ctx.font = `9px ${mono}`;
      ctx.fillText(b.band, cx, H - padB + 4);
      ctx.fillText(`n=${b.trades}`, cx, H - padB + 14);
    });

    // win-rate line (0–100% mapped to full plot height)
    const pts = bands.map((b, i) => (b.trades ? { x: padL + i * gw + gw / 2, y: padT + (1 - b.win_rate / 100) * (H - padT - padB), wr: b.win_rate } : null));
    ctx.strokeStyle = "#F5A623"; ctx.lineWidth = 1.5; ctx.beginPath();
    let started = false;
    for (const p of pts) { if (!p) { started = false; continue; } if (!started) { ctx.moveTo(p.x, p.y); started = true; } else ctx.lineTo(p.x, p.y); }
    ctx.stroke();
    for (const p of pts) {
      if (!p) continue;
      ctx.fillStyle = "#F5A623"; ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#F5A623"; ctx.textAlign = "center"; ctx.textBaseline = "bottom"; ctx.font = `9px ${mono}`;
      ctx.fillText(`${p.wr.toFixed(0)}%`, p.x, p.y - 5);
    }
  }, [bands]);
  return (
    <div className="p-3">
      <canvas ref={ref} data-testid="an-band-chart" className="w-full h-56 block" />
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground mt-1">
        <span className="flex items-center gap-1"><span className="h-2 w-3 bg-buy/80 rounded-sm" />avg R (green +, red −)</span>
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 bg-primary" />win rate</span>
        <span className="flex items-center gap-1"><span className="h-2 w-3 bg-primary/20 rounded-sm" />trade count</span>
      </div>
    </div>
  );
}
